import { useState, useEffect, useRef, useCallback, memo } from "react";
import { MapPin, Crosshair, ArrowUp, ArrowDown, Layers } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { t } from "@/translations";

const TIER_ZONES = [
  { id: "center", rows: [44, 63], cols: [44, 63], color: "#F59E0B", tier: 1 },
  { id: "cornerTL", rows: [0, 14], cols: [0, 14], color: "#0EA5E9", tier: 2 },
  { id: "cornerTR", rows: [0, 14], cols: [85, 99], color: "#0EA5E9", tier: 2 },
  { id: "topBand", rows: [0, 14], cols: [15, 84], color: "#FF6B6B", tier: 3 },
  { id: "clusterTL", rows: [0, 2], cols: [0, 2], color: "#14B8A6", tier: 4 },
  { id: "clusterTR", rows: [0, 2], cols: [97, 99], color: "#14B8A6", tier: 4 },
  { id: "clusterBL1", rows: [97, 99], cols: [0, 2], color: "#14B8A6", tier: 4 },
  { id: "clusterBR1", rows: [97, 99], cols: [97, 99], color: "#14B8A6", tier: 4 },
  { id: "clusterBL2", rows: [997, 999], cols: [0, 2], color: "#14B8A6", tier: 4 },
  { id: "clusterBR2", rows: [997, 999], cols: [97, 99], color: "#14B8A6", tier: 4 },
];

const QUICK_ZONES = [
  { key: "top", row: 0, icon: "up" },
  { key: "centerStage", row: 44, icon: "crosshair" },
  { key: "bottomClusters", row: 990, icon: "layers" },
  { key: "bottom", row: 999, icon: "down" },
];

const MINIMAP_WIDTH = 40;
const MINIMAP_HEIGHT = 300;

const GridNavigator = memo(({ scrollContainerRef, visibleRange, blocks, cellSize, totalRows, totalCols }) => {
  const { lang } = useLanguage();
  const canvasRef = useRef(null);
  const [jumpRow, setJumpRow] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const scaleX = MINIMAP_WIDTH / totalCols;
  const scaleY = MINIMAP_HEIGHT / totalRows;

  const scrollToRow = useCallback((row) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const targetY = Math.max(0, row * cellSize - el.clientHeight / 3);
    el.scrollTo({ top: targetY, behavior: "smooth" });
  }, [scrollContainerRef, cellSize]);

  const handleJump = useCallback((e) => {
    e.preventDefault();
    const row = parseInt(jumpRow, 10);
    if (isNaN(row) || row < 1 || row > totalRows) return;
    scrollToRow(row - 1);
    setJumpRow("");
  }, [jumpRow, totalRows, scrollToRow]);

  // Draw mini-map
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = MINIMAP_WIDTH * dpr;
    canvas.height = MINIMAP_HEIGHT * dpr;
    ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = "#1E293B";
    ctx.fillRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    // Tier zones
    TIER_ZONES.forEach(({ rows, cols, color }) => {
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.25;
      const x = cols[0] * scaleX;
      const y = rows[0] * scaleY;
      const w = (cols[1] - cols[0] + 1) * scaleX;
      const h = (rows[1] - rows[0] + 1) * scaleY;
      ctx.fillRect(x, y, Math.max(w, 1), Math.max(h, 1));
    });
    ctx.globalAlpha = 1;

    // Sold blocks
    Object.values(blocks).forEach((b) => {
      if (b.status === "approved" || b.status === "courtesy") {
        ctx.fillStyle = b.image_url ? "#14B8A6" : "#64748B";
        const x = b.col * scaleX;
        const y = b.row * scaleY;
        ctx.fillRect(x, y, Math.max(scaleX, 1), Math.max(scaleY, 0.5));
      }
    });

    // Viewport indicator
    const vpTop = visibleRange.start * scaleY;
    const vpHeight = Math.max((visibleRange.end - visibleRange.start) * scaleY, 4);
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(0.5, vpTop, MINIMAP_WIDTH - 1, vpHeight);
    ctx.fillStyle = "rgba(245, 158, 11, 0.08)";
    ctx.fillRect(0.5, vpTop, MINIMAP_WIDTH - 1, vpHeight);

  }, [visibleRange, blocks, scaleX, scaleY]);

  // Click/drag on mini-map
  const handleMinimapClick = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const row = Math.floor((y / MINIMAP_HEIGHT) * totalRows);
    scrollToRow(Math.max(0, Math.min(row, totalRows - 1)));
  }, [totalRows, scrollToRow]);

  const handleMinimapMouseDown = useCallback((e) => {
    setIsDragging(true);
    handleMinimapClick(e);
  }, [handleMinimapClick]);

  const handleMinimapMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMinimapClick(e);
  }, [isDragging, handleMinimapClick]);

  const handleMinimapMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mouseup", handleMinimapMouseUp);
      window.addEventListener("mousemove", handleMinimapMouseMove);
      return () => {
        window.removeEventListener("mouseup", handleMinimapMouseUp);
        window.removeEventListener("mousemove", handleMinimapMouseMove);
      };
    }
  }, [isDragging, handleMinimapMouseUp, handleMinimapMouseMove]);

  const currentRow = visibleRange.start;
  const progressPct = Math.round((currentRow / totalRows) * 100);

  if (isCollapsed) {
    return (
      <div className="grid-nav-collapsed" data-testid="grid-nav-collapsed">
        <button
          onClick={() => setIsCollapsed(false)}
          className="grid-nav-expand-btn"
          data-testid="grid-nav-expand-btn"
          title={t("gridNav.expand", lang)}
        >
          <MapPin size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="grid-nav-sidebar" data-testid="grid-nav-sidebar">
      {/* Header */}
      <div className="grid-nav-header">
        <MapPin size={13} className="text-[#F59E0B]" />
        <span className="grid-nav-title">{t("gridNav.title", lang)}</span>
        <button
          onClick={() => setIsCollapsed(true)}
          className="grid-nav-collapse-btn"
          data-testid="grid-nav-collapse-btn"
          title={t("gridNav.collapse", lang)}
        >
          <span>&times;</span>
        </button>
      </div>

      {/* Row Progress */}
      <div className="grid-nav-progress" data-testid="grid-nav-progress">
        <span className="text-[#94A3B8] text-[10px]">{t("gridNav.row", lang)} {currentRow + 1}</span>
        <div className="grid-nav-progress-bar">
          <div className="grid-nav-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="text-[#64748B] text-[10px]">{progressPct}%</span>
      </div>

      {/* Mini-map */}
      <div className="grid-nav-minimap-wrap" data-testid="grid-nav-minimap">
        <canvas
          ref={canvasRef}
          style={{ width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT, cursor: "pointer" }}
          onMouseDown={handleMinimapMouseDown}
        />
      </div>

      {/* Quick Zone Buttons */}
      <div className="grid-nav-zones" data-testid="grid-nav-zones">
        {QUICK_ZONES.map(({ key, row, icon }) => (
          <button
            key={key}
            onClick={() => scrollToRow(row)}
            className="grid-nav-zone-btn"
            data-testid={`grid-nav-zone-${key}`}
            title={t(`gridNav.${key}`, lang)}
          >
            {icon === "up" && <ArrowUp size={11} />}
            {icon === "crosshair" && <Crosshair size={11} />}
            {icon === "layers" && <Layers size={11} />}
            {icon === "down" && <ArrowDown size={11} />}
            <span>{t(`gridNav.${key}`, lang)}</span>
          </button>
        ))}
      </div>

      {/* Jump to Row */}
      <form onSubmit={handleJump} className="grid-nav-jump" data-testid="grid-nav-jump">
        <input
          type="number"
          min="1"
          max={totalRows}
          value={jumpRow}
          onChange={(e) => setJumpRow(e.target.value)}
          placeholder={t("gridNav.jumpPlaceholder", lang)}
          className="grid-nav-jump-input"
          data-testid="grid-nav-jump-input"
        />
        <button type="submit" className="grid-nav-jump-btn" data-testid="grid-nav-jump-btn">
          {t("gridNav.go", lang)}
        </button>
      </form>
    </div>
  );
});

GridNavigator.displayName = "GridNavigator";
export default GridNavigator;
